import mammoth from 'mammoth';
import * as pdfjs from 'pdfjs-dist';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_EXT = ['.txt', '.md', '.docx', '.pdf'];

export interface ExtractedFile {
  text: string;
  fileName: string;
  mimeType: string;
  base64: string;
}

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
}

async function readAsBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

async function extractPdfText(data: ArrayBuffer): Promise<string> {
  const doc = await pdfjs.getDocument({ data }).promise;
  const parts: string[] = [];
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const content = await page.getTextContent();
    const pageText = content.items
      .map((item) => ('str' in item ? item.str : ''))
      .join(' ');
    parts.push(pageText);
  }
  return parts.join('\n\n').trim();
}

export async function extractStep1UploadFile(file: File): Promise<ExtractedFile> {
  if (file.size > MAX_BYTES) {
    throw new Error('File must be 5 MB or smaller.');
  }
  const ext = extOf(file.name);
  if (!ALLOWED_EXT.includes(ext)) {
    throw new Error('Supported formats: .txt, .md, .docx, .pdf');
  }

  const base64 = await readAsBase64(file);
  const buffer = await file.arrayBuffer();
  let text = '';

  if (ext === '.txt' || ext === '.md') {
    text = new TextDecoder('utf-8').decode(buffer);
  } else if (ext === '.docx') {
    const result = await mammoth.extractRawText({ arrayBuffer: buffer });
    text = result.value || '';
  } else if (ext === '.pdf') {
    text = await extractPdfText(buffer);
  }

  if (!text.trim()) {
    throw new Error('Could not extract text from this file.');
  }

  return {
    text: text.trim(),
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    base64,
  };
}
